/**
 * Сервис для автоматической синхронизации фотографий с сервером
 * Управляет очередью загрузки и повторными попытками при ошибках
 */

import {
  getPendingPhotos,
  updatePhotoStatus,
  type PhotoRecord,
} from '../utils/imageStorage';
import { isRequestHalted } from '../utils/requestHalt';

export type SyncEventType = 'started' | 'progress' | 'completed' | 'failed' | 'photo-uploaded';

export interface SyncEvent {
  type: SyncEventType;
  photoId?: string;
  total?: number;
  current?: number;
  error?: string;
}

type SyncListener = (event: SyncEvent) => void;

class PhotoSyncService {
  private isSyncing = false;
  private listeners: Set<SyncListener> = new Set();
  private uploadFunction: ((photo: PhotoRecord) => Promise<string>) | null = null;
  private autoSyncInterval: number | null = null;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 5000; // 5 секунд
  private readonly AUTO_SYNC_INTERVAL = 30000; // 30 секунд

  /**
   * Инициализация сервиса с функцией загрузки
   */
  initialize(uploadFunction: (photo: PhotoRecord) => Promise<string>) {
    this.uploadFunction = uploadFunction;
    this.startAutoSync();
    this.setupNetworkListeners();
  }

  /**
   * Подписка на события синхронизации
   */
  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Отправка события всем подписчикам
   */
  private emit(event: SyncEvent) {
    this.listeners.forEach((listener) => listener(event));
  }

  /**
   * Проверка наличия интернет-соединения
   */
  private isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Запуск автоматической синхронизации по таймеру
   */
  private startAutoSync() {
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
    }

    this.autoSyncInterval = window.setInterval(() => {
      if (!isRequestHalted() && this.isOnline() && !this.isSyncing) {
        this.sync();
      }
    }, this.AUTO_SYNC_INTERVAL);
  }

  /**
   * Остановка автоматической синхронизации
   */
  stopAutoSync() {
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
      this.autoSyncInterval = null;
    }
  }

  /**
   * Настройка слушателей событий сети
   */
  private setupNetworkListeners() {
    window.addEventListener('online', () => {
      console.log('🌐 Интернет восстановлен, запускаем синхронизацию');
      if (!isRequestHalted()) {
        this.sync();
      }
    });

    window.addEventListener('offline', () => {
      console.log('📵 Интернет отключён');
    });
  }

  /**
   * Основной метод синхронизации
   */
  async sync(forceRetry: boolean = false): Promise<void> {
    if (this.isSyncing) {
      console.log('Синхронизация уже выполняется');
      return;
    }

    if (isRequestHalted()) {
      console.warn('Синхронизация остановлена до перезагрузки страницы');
      return;
    }

    if (!this.isOnline()) {
      console.log('Нет подключения к интернету');
      return;
    }

    if (!this.uploadFunction) {
      console.error('Upload function не инициализирована');
      return;
    }

    try {
      this.isSyncing = true;
      this.emit({ type: 'started' });

      const pendingPhotos = await getPendingPhotos();

      if (pendingPhotos.length === 0) {
        this.emit({ type: 'completed', total: 0, current: 0 });
        return;
      }

      console.log(`📤 Начинаем загрузку ${pendingPhotos.length} фото${forceRetry ? ' (принудительная попытка)' : ''}`);

      let successCount = 0;
      let failedCount = 0;

      for (let i = 0; i < pendingPhotos.length; i++) {
        if (isRequestHalted()) {
          console.warn('Синхронизация прервана после критической серверной ошибки');
          break;
        }

        const photo = pendingPhotos[i];

        // При принудительной синхронизации (ручной клик) - пропускаем проверку лимита попыток
        if (!forceRetry && photo.retryCount >= this.MAX_RETRIES) {
          console.warn(`⚠️ Фото ${photo.id} превысило лимит попыток (используйте ручную синхронизацию)`);
          failedCount++;
          continue;
        }

        try {
          await this.uploadPhoto(photo);
          successCount++;
          
          this.emit({
            type: 'photo-uploaded',
            photoId: photo.id,
            current: i + 1,
            total: pendingPhotos.length,
          });
        } catch (error) {
          console.error(`❌ Ошибка загрузки фото ${photo.id}:`, error);
          failedCount++;
          
          this.emit({
            type: 'failed',
            photoId: photo.id,
            error: error instanceof Error ? error.message : 'Unknown error',
          });

          if (isRequestHalted()) {
            console.warn('Дальнейшая синхронизация остановлена до перезагрузки страницы');
            break;
          }
        }

        this.emit({
          type: 'progress',
          current: i + 1,
          total: pendingPhotos.length,
        });
      }

      console.log(`✅ Синхронизация завершена: ${successCount} успешно, ${failedCount} с ошибками`);
      
      this.emit({
        type: 'completed',
        total: pendingPhotos.length,
        current: successCount,
      });
    } catch (error) {
      console.error('Критическая ошибка синхронизации:', error);
      this.emit({
        type: 'failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Загрузка одной фотографии
   */
  private async uploadPhoto(photo: PhotoRecord): Promise<void> {
    if (!this.uploadFunction) {
      throw new Error('Upload function не инициализирована');
    }

    // Обновляем статус на "uploading"
    await updatePhotoStatus(photo.id, 'uploading');

    try {
      // Вызываем функцию загрузки, передаём всю информацию о фото
      const remoteUrl = await this.uploadFunction(photo);

      // Удаляем фото из IndexedDB после успешной загрузки (оно уже на сервере)
      const { deletePhoto } = await import('../utils/imageStorage');
      await deletePhoto(photo.id);
      
      console.log(`✅ Фото ${photo.id} успешно загружено и удалено из локального хранилища: ${remoteUrl}`);
    } catch (error) {
      // Если 403 - недостаточно прав, удаляем фото из очереди и показываем уведомление
      if (error instanceof Error && (error as any).isForbidden) {
        const { deletePhoto } = await import('../utils/imageStorage');
        await deletePhoto(photo.id);
        
        // Показываем toast с сообщением об ошибке прав
        const { message } = await import('antd');
        message.error('Недостаточно прав для загрузки фото');
        
        console.warn(`⚠️ Фото ${photo.id} удалено из очереди: недостаточно прав`);
        return; // Не пробрасываем ошибку дальше, т.к. уже обработали
      }
      
      // В случае других ошибок помечаем как failed для повторной попытки
      await updatePhotoStatus(photo.id, 'failed', {
        error: error instanceof Error ? error.message : 'Upload failed',
      });
      
      throw error;
    }
  }

  /**
   * Принудительная синхронизация конкретного фото
   */
  async syncPhoto(photoId: string): Promise<void> {
    if (!this.uploadFunction) {
      throw new Error('Upload function не инициализирована');
    }

    const { getPhotoById } = await import('../utils/imageStorage');
    const photo = await getPhotoById(photoId);

    if (!photo) {
      throw new Error(`Фото с id ${photoId} не найдено`);
    }

    await this.uploadPhoto(photo);
  }

  /**
   * Получение статуса синхронизации
   */
  getStatus(): { isSyncing: boolean; isOnline: boolean } {
    return {
      isSyncing: this.isSyncing,
      isOnline: this.isOnline(),
    };
  }

  /**
   * Остановка сервиса
   */
  destroy() {
    this.stopAutoSync();
    this.listeners.clear();
    this.uploadFunction = null;
  }
}

// Singleton экземпляр сервиса
export const photoSyncService = new PhotoSyncService();
